import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  CircleDashed,
  LockKeyhole,
  Radio,
  ShieldCheck,
} from "lucide-react";
import { useConsoleContext } from "../shell";

const ladder = [
  ["L0", "Silence", "AVAILABLE"],
  ["L1", "Passive", "LOCKED"],
  ["L2", "Nudge", "LOCKED"],
  ["L3", "One question", "LOCKED"],
  ["L4", "AI adviser", "PILOT 2"],
  ["L5", "Action", "PILOT 2"],
  ["L6", "Human", "PILOT 2"],
];

export function OverviewPage() {
  const { activeOrganization, sites: clientSites } = useConsoleContext();
  const sites = clientSites.map((site) => {
    const progress = {
      draft: 20,
      verifying: 30,
      shadow: 60,
      live: 100,
      paused: 60,
    }[site.status];
    return {
      domain: site.origin?.replace(/^https?:\/\//, "") ?? site.name,
      mode: site.status === "live" ? "LIVE" : site.status === "shadow" ? "PROVE NEXT" : "CONNECT NEXT",
      detail:
        site.outcome_type === "booking"
          ? "Lodgify outcome authorization pending · rendering locked"
          : "Qualified outcome mapping pending · rendering locked",
      progress,
    };
  });
  return (
    <div className="page-frame">
      <section className="page-heading">
        <div>
          <p className="eyebrow">
            {activeOrganization.accessSource === "beta" ? "FOUNDING DEPLOYMENT" : "CLIENT DEPLOYMENT"} · {String(sites.length).padStart(2, "0")}
          </p>
          <h1>Earn the right to intervene.</h1>
          <p className="page-intro">
            Connect the first journey, prove the event contract, then watch
            decisions in shadow mode before a visitor sees anything.
          </p>
        </div>
        <Link to="/sites" className="primary-action">
          Continue setup <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </section>

      <section className="proof-strip" aria-label="Foundation status">
        <div className="proof-main">
          <p>Verified production traffic</p>
          <strong>Awaiting first event</strong>
          <span>
            SDK and outcome receipt must both pass before shadow mode begins.
          </span>
        </div>
        <div className="proof-stat">
          <p>Permanent holdout</p>
          <strong>20%</strong>
          <span>Never zero</span>
        </div>
        <div className="proof-stat">
          <p>Rendered</p>
          <strong>0</strong>
          <span>Locked by policy</span>
        </div>
        <div className="proof-stat">
          <p>Coverage</p>
          <strong>0 / {sites.length}</strong>
          <span>This client</span>
        </div>
      </section>

      <div className="content-grid">
        <section className="deployment-panel">
          <header className="section-heading">
            <div>
              <p className="eyebrow">DEPLOYMENT RUNWAY</p>
              <h2>{sites.length === 1 ? "One site, one evidence boundary" : "Every site has its own evidence boundary"}</h2>
            </div>
            <span className="status-chip amber">SHADOW NEXT</span>
          </header>

          <div className="site-runway">
            {sites.map((site, index) => (
              <div className="runway-row" key={site.domain}>
                <span className="runway-index">0{index + 1}</span>
                <div className="runway-copy">
                  <b>{site.domain}</b>
                  <span>{site.detail}</span>
                </div>
                <div
                  className="runway-progress"
                  aria-label={`${site.progress}% complete`}
                >
                  <i style={{ width: `${site.progress}%` }} />
                </div>
                <small>{site.mode}</small>
              </div>
            ))}
          </div>
          <footer className="panel-note">
            <Radio size={15} aria-hidden="true" />
            Other beta clients live in separate organizations and never appear
            in this tenant view.
          </footer>
        </section>

        <aside className="evidence-panel">
          <p className="eyebrow">EVIDENCE CONTRACT</p>
          <h2>What must be true</h2>
          <ol className="evidence-list">
            <li className="is-complete">
              <span>
                <Check size={13} />
              </span>
              <div>
                <b>Cloudflare foundation</b>
                <small>Account and runtime verified</small>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <b>Domain ownership</b>
                <small>DNS or meta-tag receipt</small>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <b>Consent mapping</b>
                <small>Analytics events only when allowed</small>
              </div>
            </li>
            <li>
              <span>4</span>
              <div>
                <b>Outcome receipt</b>
                <small>Lead or purchase reaches the queue</small>
              </div>
            </li>
            <li>
              <span>5</span>
              <div>
                <b>Kill-switch drill</b>
                <small>One flag resolves every decision to L0</small>
              </div>
            </li>
          </ol>
        </aside>
      </div>

      <section className="ladder-section">
        <header className="section-heading">
          <div>
            <p className="eyebrow">POLICY CEILING</p>
            <h2>The ladder starts with restraint</h2>
          </div>
          <span className="policy-lock">
            <LockKeyhole size={13} /> L0 ENFORCED
          </span>
        </header>
        <div className="ladder-grid">
          {ladder.map(([level, name, state], index) => (
            <div className={`ladder-step level-${index}`} key={level}>
              <span>{level}</span>
              <b>{name}</b>
              <small>{state}</small>
            </div>
          ))}
        </div>
        <div className="distribution-line">
          <span style={{ width: "91.1%" }} />
          <i />
        </div>
        <p className="distribution-caption">
          TARGET DISTRIBUTION · AT LEAST 9 IN 10 ELIGIBLE JOURNEYS REMAIN
          UNTOUCHED
        </p>
      </section>

      <section className="decision-empty">
        <div className="empty-orbit">
          <CircleDashed size={26} />
        </div>
        <div>
          <p className="eyebrow">DECISION LEDGER</p>
          <h2>No production receipts yet—and no invented proof.</h2>
          <p>
            The first row appears only after a verified SDK event passes consent
            and origin checks.
          </p>
        </div>
        <div className="guardrail-copy">
          <ShieldCheck size={17} />
          <span>
            <b>Safe default</b>
            <small>Missing data always resolves to silence.</small>
          </span>
        </div>
      </section>
    </div>
  );
}
